import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppCard, AppCardContent, AppDataTable, FilterToolbar, StatusBadge } from "../../../ui";
import Shimmer from "../../../Common/Shimmer";
import apiClient from "../../../../utils/apiClient";
import { DASH, formatDate, formatDuration, formatTime } from "../../../../utils/format";
import RecordCardHead, { TableHeading } from "../RecordCardHead";
import RecordStat from "../RecordStat";
import { useResource } from "../useStudentRecord";

const PAGE_SIZE = 20;

const span = (from, to) => (from ? `${formatTime(from)} – ${to ? formatTime(to) : "…"}` : DASH);

const COLUMNS = [
  {
    key: "date",
    label: "Date",
    render: (s) => <span className="tw-font-medium tw-text-content">{formatDate(s.sessionStart || s.sessionDate)}</span>,
  },
  { key: "batch", label: "Batch", render: (s) => s.batchName || DASH },
  {
    key: "classTime",
    label: "Class time",
    render: (s) => <span className="tw-tabular-nums tw-text-content-muted">{span(s.sessionStart, s.sessionEnd)}</span>,
  },
  {
    key: "joined",
    label: "Joined – left",
    render: (s) => <span className="tw-tabular-nums tw-text-content-muted">{span(s.joinedAt, s.leftAt)}</span>,
  },
  {
    key: "minutes",
    label: "Time in class",
    align: "right",
    render: (s) => <span className="tw-tabular-nums">{s.activeMinutes ? formatDuration(s.activeMinutes * 60) : DASH}</span>,
  },
  {
    key: "share",
    label: "Of the class",
    align: "right",
    render: (s) => <span className="tw-tabular-nums">{s.attendancePct ? `${Math.round(s.attendancePct)}%` : DASH}</span>,
  },
  {
    key: "status",
    label: "Status",
    render: (s) => <StatusBadge status={s.attendanceStatus} domain="attendance" />,
  },
];

/**
 * AttendanceTab — how often the student comes to class, and every class on
 * record.
 *
 * ⚠️ Two sources, and they cover different ground. The summary is the
 * dashboard's figure for the PRIMARY batch: present ÷ classes of 15+ minutes
 * held in it. The log is every session in which this student has an attendance
 * record, across every batch — a class they never joined has no record, so it is
 * counted as missed above and does not appear below. The card says so rather
 * than let the two be read as the same list.
 */
const AttendanceTab = ({ studentId, stats, attendance, batchName }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const log = useResource(`${studentId}:attendance:${page}`, () =>
    apiClient.get(`/attendance/student/${studentId}`, { params: { page, limit: PAGE_SIZE } })
      .then((r) => r.data));

  const att = stats.data?.attendance;
  const notAttended = Math.max(attendance.held - attendance.present - attendance.partial, 0);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <RecordCardHead
          title="Attendance"
          description={batchName
            ? `For ${batchName}, against a ${attendance.minPct}% minimum. The log below covers every batch.`
            : "Not in a batch, so there is no attendance to measure yet."}
        />
        <AppCardContent>
          {stats.loading && !stats.data ? (
            <Shimmer height={64} />
          ) : (
            <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-5 md:tw-grid-cols-3 min-[1200px]:tw-grid-cols-6">
              <RecordStat
                label="Attendance"
                value={attendance.percent === null ? DASH : `${attendance.percent}%`}
                tone={attendance.held ? attendance.tone : undefined}
                hint={`minimum ${attendance.minPct}%`}
              />
              <RecordStat label="Present" value={attendance.present} hint={`of ${attendance.held} classes held`} />
              <RecordStat label="Partial" value={attendance.partial} hint="joined for part of the class" />
              <RecordStat label="Not attended" value={notAttended} hint="absent, excused or not joined" />
              <RecordStat
                label="Time in class"
                value={att?.totalActiveMinutes ? formatDuration(att.totalActiveMinutes * 60) : DASH}
                hint="across those classes"
              />
              <RecordStat
                label="Of each class"
                value={att?.sessionsAttended ? `${att.avgSessionPct}%` : DASH}
                hint="on average, when they joined"
              />
            </dl>
          )}
        </AppCardContent>
      </AppCard>

      <AppDataTable
        fill={false}
        toolbar={(
          <FilterToolbar flush>
            <TableHeading
              title="Class log"
              count={log.data?.total ? `${log.data.total} session${log.data.total === 1 ? "" : "s"}` : ""}
            />
          </FilterToolbar>
        )}
        columns={COLUMNS}
        rows={log.data?.sessions || []}
        rowKey={(s) => s._id}
        loading={log.loading}
        onRowClick={(s) => navigate(`/attendance/session/${s._id}`)}
        emptyMessage={log.error ? "Could not load the class log." : "No class sessions recorded for this student yet."}
        pagination={{
          page,
          totalPages: log.data?.totalPages || 1,
          total: log.data?.total || 0,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: "sessions",
        }}
      />
    </div>
  );
};

export default AttendanceTab;
